// components/ProductPage/productTypes/registry.tsx
import type { ComponentType, FC } from 'react'

/**
 * Product registry - exports: ProductKey, ProductUiEntry, PRODUCT_UI, isProductKey
 */
export type ProductKey = 'stand' | 'wardrobe' | 'wardrobe-new-collection' | 'tv-stand' | 'bedside' | 'rack'

/** No props needed right now; keeps it strictly typed and uniform */
type ShellProps = Record<string, never>

export interface ProductUiEntry {
  titleKey: string
  Shell: ComponentType<ShellProps>
}

/* ---- Bedside ---- */
import { ProductPage as BedsideProductPage } from '~/components/ProductPage/BedsideProductPage'
import { BedsideProductConfigurator } from '~/components/ProductPage/productTypes/bedside'

const BedsideShell: FC<ShellProps> = () => (
  <BedsideProductPage
    components={BedsideProductConfigurator}
    name="homepage.configurator.bedside.title"
  />
)

/* ---- Stand ---- */
import { ProductPage as StandProductPage } from '~/components/ProductPage/StandProductPage'
import { StandProductConfigurator } from '~/components/ProductPage/productTypes/stand'

const StandShell: FC<ShellProps> = () => (
  <StandProductPage
    components={StandProductConfigurator}
    name="homepage.configurator.stand.title"
  />
)

/* ---- Wardrobe ---- */
import { ProductPage as WardrobeProductPage } from '~/components/ProductPage/WardrobeProductPage'
import { WardrobeProductConfigurator } from '~/components/ProductPage/productTypes/wardrobe'

const WardrobeShell: FC<ShellProps> = () => (
  <WardrobeProductPage
    components={WardrobeProductConfigurator}
    name="homepage.configurator.wardrobe.title"
  />
)

/* ---- TV-stand ---- */
import { ProductPage as TVStandProductPage } from '~/components/ProductPage/TVStandProductPage'
import { TVStandProductConfigurator } from '~/components/ProductPage/productTypes/TVstand'

const TVStandShell: FC<ShellProps> = () => (
  <TVStandProductPage
    components={TVStandProductConfigurator}
    name="homepage.configurator.tv-stand.title"
  />
)

/* ---- Rack ---- */
import { ProductPage as RackProductPage } from '~/components/ProductPage/RackProductPage'
import { RackProductConfigurator } from '~/components/ProductPage/productTypes/rack'

const RackShell: FC<ShellProps> = () => (
  <RackProductPage
    components={RackProductConfigurator}
    name="homepage.configurator.rack.title"
  />
)

/* ---- Wardrobe New Collection ---- */
import { ProductPage as WardrobeNewCollectionProductPage } from '~/components/ProductPage/WardrobeProductPage'
import { WardrobeNewCollectionConfigurator } from '~/components/ProductPage/productTypes/wardrobeNewCollection'

const WardrobeNewCollectionShell: FC<ShellProps> = () => (
  <WardrobeNewCollectionProductPage
    components={WardrobeNewCollectionConfigurator}
    name="homepage.configurator.wardrobe-new-collection.title"
    furnitureType="wardrobe-new-collection"
  />
)

export const PRODUCT_UI: Record<ProductKey, ProductUiEntry> = {
  bedside: {
    titleKey: 'homepage.configurator.bedside.title',
    Shell: BedsideShell,
  },
  stand: { titleKey: 'homepage.configurator.stand.title', Shell: StandShell },
  wardrobe: {
    titleKey: 'homepage.configurator.wardrobe.title',
    Shell: WardrobeShell,
  },
  'wardrobe-new-collection': {
    titleKey: 'homepage.configurator.wardrobe-new-collection.title',
    Shell: WardrobeNewCollectionShell,
  },
  'tv-stand': {
    titleKey: 'homepage.configurator.tvstand.title',
    Shell: TVStandShell,
  },
  rack: {
    titleKey: 'homepage.configurator.rack.title',
    Shell: RackShell,
  },
}

export const isProductKey = (v: string): v is ProductKey =>
  (['stand', 'wardrobe', 'wardrobe-new-collection', 'tv-stand', 'bedside', 'rack'] as const).includes(
    v as ProductKey
  )
